/**
 * Terms of Service — a *notice* surface, not a legal document.
 *
 * `RegisterPage` gates submission on the user affirming they accept the Terms
 * of Service and the Privacy Policy, so those two links have to resolve to
 * something a person can actually read. This route exists so the consent line
 * is never a dead end. It deliberately contains NO legal text and says plainly
 * that the document has not been published.
 *
 * NEXT DEVELOPER — once the real documents are hosted somewhere, set
 * `VITE_TERMS_URL` (and `VITE_PRIVACY_URL`) in the deployment environment.
 * `RegisterPage` reads both at render time and links straight out to them in a
 * new tab, at which point this placeholder route stops being reachable from the
 * sign-up form. Delete it once the documents ship.
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { motion, type Variants } from 'framer-motion'
import { ArrowLeft, FileDashed } from '@phosphor-icons/react'
import { BrandWordmark } from '@/components/ui/Logomark'

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const stageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

/* Type objects lifted verbatim from ForgotPasswordPage so the legal pages read
   as the same screen family as the auth flow they are linked from. */
const displayStyle = {
  fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
  fontSize: '36px',
  lineHeight: 1.05,
  letterSpacing: '-0.8px',
  fontWeight: 400,
} as const

const leadStyle = {
  fontSize: '15px',
  lineHeight: 1.5,
  letterSpacing: '-0.165px',
  fontWeight: 400,
} as const

const bodyStyle = {
  fontSize: '13.5px',
  lineHeight: 1.6,
  letterSpacing: '-0.13px',
  fontWeight: 400,
} as const

const eyebrowStyle = {
  fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontSize: '10px',
  fontWeight: 400,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
} as const

const actionStyle = { fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' } as const

/**
 * The shared shell for the two unpublished-document notices. Kept inline
 * rather than extracted so `/terms` and `/privacy` stay self-contained, single
 * -file routes that can be deleted whole once the real documents exist.
 */
const LegalNotice: React.FC<{
  icon: React.ReactNode
  titleLead: string
  titleAccent: string
  children: React.ReactNode
}> = ({ icon, titleLead, titleAccent, children }) => (
  <div className="min-h-[100dvh] flex items-center justify-center px-6 py-12 bg-[#fafafa] dark:bg-[#08090a]">
    <motion.main
      className="w-full max-w-[440px]"
      variants={stageVariants}
      initial="hidden"
      animate="show"
    >
      {/* Brand mark — these pages open in their own tab, so they have to
          identify themselves without the auth shell around them. */}
      <motion.div variants={itemVariants} className="mb-9">
        <Link
          to="/login"
          aria-label="Beevelope — back to sign in"
          className="inline-flex items-center gap-2.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fafafa] dark:focus-visible:ring-offset-[#08090a]"
        >
          <BrandWordmark height={26} alt="" />
        </Link>
      </motion.div>

      <motion.div variants={itemVariants} className="mb-8">
        <div className="h-12 w-12 rounded-md bg-[rgba(15,16,17,0.04)] border border-[rgba(15,16,17,0.06)] dark:bg-white/[0.04] dark:border-white/[0.05] flex items-center justify-center mb-5">
          {icon}
        </div>
        <p className="text-[#5f6166] dark:text-[#8a8f98] mb-2.5" style={eyebrowStyle}>
          Not yet published
        </p>
        <h1 className="text-[#0f1011] dark:text-[#f7f8f8]" style={displayStyle}>
          {titleLead} <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">{titleAccent}</span>
        </h1>
        <p className="text-[#5f6166] dark:text-[#8a8f98] mt-2" style={leadStyle}>
          We haven&rsquo;t published this document yet.
        </p>
      </motion.div>

      {children}
    </motion.main>
  </div>
)

const TermsPage: React.FC = () => {
  React.useEffect(() => {
    const previous = document.title
    document.title = 'Terms of Service · Beevelope'
    return () => {
      document.title = previous
    }
  }, [])

  return (
    <LegalNotice
      titleLead="Terms of"
      titleAccent="Service"
      icon={<FileDashed className="h-5 w-5 text-[#5f6166] dark:text-[#8a8f98]" weight="regular" />}
    >
      <motion.div
        variants={itemVariants}
        className="rounded-lg bg-white border border-black/[0.06] dark:bg-white/[0.02] dark:border-white/[0.08] p-5 space-y-3.5"
      >
        <p className="text-[#5f6166] dark:text-[#8a8f98]" style={bodyStyle}>
          There is no Terms of Service document to read here. This page is a notice, not an
          agreement — nothing on it sets out terms and nothing on it binds you.
        </p>
        <p className="text-[#5f6166] dark:text-[#8a8f98]" style={bodyStyle}>
          A copy will be provided on request once the document is published. If you would rather
          not create an account before reading it, you do not have to.
        </p>
      </motion.div>

      <motion.p
        variants={itemVariants}
        className="text-[#5f6166] dark:text-[#8a8f98] mt-4"
        style={{ fontSize: '12.5px', lineHeight: 1.55, letterSpacing: '-0.13px', fontWeight: 400 }}
      >
        Opened this from the sign-up form? That form is still waiting in your other tab.
      </motion.p>

      <motion.div variants={itemVariants} className="mt-7">
        <Link
          to="/login"
          className="w-full h-10 rounded-md bg-white border border-black/[0.08] text-[#0f1011] hover:bg-[#f5f5f5] hover:border-black/[0.14] dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.12] inline-flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fafafa] dark:focus-visible:ring-offset-[#08090a] transition-colors duration-150"
          style={actionStyle}
        >
          <ArrowLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
          Back to sign in
        </Link>
      </motion.div>
    </LegalNotice>
  )
}

export default TermsPage
